import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { ArrowRight, ArrowLeft, BookOpen, Check, ChevronDown, UserCheck, ShieldCheck } from 'lucide-react';
import { trackEvent } from '../lib/analytics';

export default function LearnMorePage() {
  const navigate = useNavigate();

  useEffect(() => {
    // Keep the funnel comparable to homepage — fire a single page-visit event
    trackEvent('homepage_visit', { path: '/learn-more' });
  }, []);

  const goToTrial = (source) => {
    trackEvent('start_free_trial_clicked', { source });
    navigate('/register?role=manager');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900">
      {/* Sticky header */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 bg-[#0B132B]/95 backdrop-blur-xl border-b border-white/10 shadow-[0_4px_24px_-8px_rgba(0,0,0,0.6)]"
        data-testid="learn-more-nav"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="learn-more-nav-home">
            <Logo size="sm" linkTo={null} variant="light" wordmarkClassName="text-white" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/80 hover:text-white"
              data-testid="learn-more-back-home"
            >
              <ArrowLeft className="h-4 w-4" /> Back to home
            </Link>
            <Button
              className="rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white px-4 sm:px-5 h-10 text-[13px] font-semibold"
              onClick={() => goToTrial('learn-more-nav')}
              data-testid="learn-more-nav-cta"
            >
              Start Free Trial
            </Button>
          </div>
        </div>
      </nav>

      <main className="pt-28 sm:pt-32 pb-16 sm:pb-20 px-5 sm:px-6" data-testid="learn-more-page">
        <div className="max-w-3xl mx-auto">
          {/* Page header */}
          <div className="flex items-center gap-3 mb-7">
            <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-gradient-to-br from-[#4338CA] to-[#3730A3] text-white shadow-md">
              <BookOpen className="h-5 w-5" />
            </span>
            <div>
              <p className="uppercase tracking-[0.18em] text-[10.5px] font-bold text-[#4338CA]">Learn more</p>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B132B] leading-tight">
                About ShiftCover365
              </h1>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 sm:px-9 py-8 sm:py-10 space-y-12">
              {/* What ShiftCover365 Does */}
              <article data-testid="learn-more-what">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B132B] mb-4">What ShiftCover365 Does</h2>
                <div className="space-y-4 text-[15px] text-slate-700 leading-relaxed">
                  <p>
                    When we spoke to business owners, three challenges came up again and again: <strong className="font-semibold text-slate-900">finding trusted staff, finding trained staff, and finding cover quickly when someone calls in sick</strong>. ShiftCover365 was built specifically around those answers.
                  </p>
                  <p>
                    Instead of agency staff or last-minute group chats, managers post a shift to <strong className="font-semibold text-slate-900">workers with relevant industry experience</strong> — people who already know the floor, who carry a visible reliability history from previous shifts, and who can be assessed before they ever walk in.
                  </p>
                  <p>
                    Every available worker is surfaced in <strong className="font-semibold text-slate-900">real time</strong>. Managers see who is free now, who has covered similar shifts, and how reliably they have turned up before approving them. The decision stays with you — we just make the trust signals visible.
                  </p>
                  <p>
                    Underneath every shift is a clear accountability record: who applied, who was approved, what time they signed in and out, and how the shift was rated by both sides. Less time on the phone, less time chasing rotas, more time running the business.
                  </p>
                  <p>
                    ShiftCover365 was initially developed alongside Post Office branches and is now expanding to support retailers, convenience stores, newsagents, betting shops and other customer-facing businesses across the UK — anywhere owners need <em>known, trained, available</em> cover.
                  </p>
                </div>
              </article>

              <hr className="border-slate-200" />

              {/* How ShiftCover365 Works */}
              <article data-testid="learn-more-how">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B132B] mb-6">How ShiftCover365 Works</h2>
                <ol className="space-y-6">
                  {[
                    {
                      title: 'Register Your Branch',
                      body: (
                        <>
                          <p>Create your account and set up your branch profile.</p>
                          <p className="mt-2 text-emerald-700 font-semibold text-[14px]">No card required.</p>
                        </>
                      ),
                    },
                    {
                      title: 'Protection & Accountability Setup',
                      body: (
                        <>
                          <p>Before creating an account, users review and accept a small number of documents designed to protect both businesses and workers.</p>
                          <p className="mt-3 font-semibold text-slate-900">These include:</p>
                          <ul className="mt-2 space-y-1.5 text-[14.5px]">
                            {[
                              'Terms of Service',
                              'Terms of Service',
                              'Privacy Policy',
                              'Branch Owner Agreement',
                              'Worker Agreement',
                              'Digital Accountability Agreement',
                            ].map((d) => (
                              <li key={d} className="flex items-start gap-2">
                                <span className="text-[#4338CA] font-bold leading-snug">•</span>
                                <span>{d}</span>
                              </li>
                            ))}
                          </ul>
                          <p className="mt-3">These documents explain how the platform works, how data is protected and how accountability records are maintained.</p>
                        </>
                      ),
                    },
                    {
                      title: 'Post A Shift',
                      body: (
                        <>
                          <p>Managers create a shift including:</p>
                          <ul className="mt-2 space-y-1.5 text-[14.5px]">
                            {['Date', 'Time', 'Branch', 'Pay Rate'].map((d) => (
                              <li key={d} className="flex items-start gap-2">
                                <span className="text-[#4338CA] font-bold leading-snug">•</span>
                                <span>{d}</span>
                              </li>
                            ))}
                          </ul>
                          <p className="mt-3">Most shifts can be created in under a minute.</p>
                        </>
                      ),
                    },
                    {
                      title: 'A Trusted Worker Applies',
                      body: (
                        <>
                          <p>Workers already linked to your branches can apply.</p>
                          <p className="mt-3 font-semibold text-slate-900">Managers can review:</p>
                          <ul className="mt-2 space-y-1.5 text-[14.5px]">
                            {['Reliability history', 'Ratings', 'Smart ID status', 'Previous shift performance'].map((d) => (
                              <li key={d} className="flex items-start gap-2">
                                <span className="text-[#4338CA] font-bold leading-snug">•</span>
                                <span>{d}</span>
                              </li>
                            ))}
                          </ul>
                          <p className="mt-3">before making a decision.</p>
                        </>
                      ),
                    },
                    {
                      title: 'Approve & Track',
                      body: (
                        <>
                          <p>Managers approve their chosen worker.</p>
                          <p className="mt-2">The shift is logged and accountability records are maintained throughout the process, providing a clear record for both the business and the worker.</p>
                        </>
                      ),
                    },
                    {
                      title: 'Share Feedback',
                      body: (
                        <p>At the end of the shift, feedback can be recorded to help build reliability history and improve future shift matching.</p>
                      ),
                    },
                  ].map((step, idx) => (
                    <li key={step.title} className="flex gap-4" data-testid={`learn-more-step-${idx + 1}`}>
                      <span className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#0B132B] text-white text-sm font-bold">{idx + 1}</span>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-[#0B132B] mb-2">Step {idx + 1} — {step.title}</h3>
                        <div className="text-[14.5px] text-slate-700 leading-relaxed">{step.body}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </article>

              <hr className="border-slate-200" />

              {/* Trust & Verification (moved here from the homepage Smart ID section) */}
              <article id="trust-and-verification" data-testid="learn-more-trust-verification">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B132B] mb-2">How We Verify Workers</h2>
                <p className="text-[14.5px] text-slate-600 leading-relaxed mb-5">
                  Every worker on ShiftCover365 goes through a clear, transparent trust process — managers always see exactly how verified a worker is before approving cover.
                </p>

                <div className="grid sm:grid-cols-3 gap-4">
                  {[
                    {
                      step: '01',
                      title: 'Identity check',
                      color: 'bg-[#4338CA]',
                      points: [
                        'Worker provides full name, photo, phone number and email at registration.',
                        'Email and phone are verified before any shift application can be submitted.',
                      ],
                    },
                    {
                      step: '02',
                      title: 'Self-declared verification',
                      color: 'bg-amber-500',
                      points: [
                        'Worker self-declares relevant industry experience and any role-specific credentials they hold.',
                        'Marked clearly as "Self-Declared" in the applicant view — no green tick yet.',
                      ],
                    },
                    {
                      step: '03',
                      title: 'Manager-confirmed verification',
                      color: 'bg-emerald-600',
                      points: [
                        'You (or your HR contact) confirm the worker\u2019s credentials through your usual verification process.',
                        'Worker is then upgraded to "Verified" — the green tick you see beside their name.',
                      ],
                    },
                  ].map((card) => (
                    <div
                      key={card.step}
                      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                      data-testid={`learn-more-trust-step-${card.step}`}
                    >
                      <div className={`inline-flex items-center justify-center h-8 px-2.5 rounded-full text-white text-[10.5px] font-bold tracking-wide ${card.color} mb-3`}>
                        STEP {card.step}
                      </div>
                      <h3 className="text-base font-bold text-[#0B132B] mb-2">{card.title}</h3>
                      <ul className="space-y-1.5">
                        {card.points.map((p, i) => (
                          <li key={i} className="flex gap-2 text-[13px] text-slate-700 leading-snug">
                            <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5" data-testid="learn-more-trust-callout">
                  <div className="flex items-center gap-2.5">
                    <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-amber-100">
                      <UserCheck className="h-4.5 w-4.5 text-amber-700" />
                    </span>
                    <div className="leading-tight">
                      <p className="text-[12.5px] font-bold text-amber-800">Self-Declared</p>
                      <p className="text-[11px] text-slate-500">Can apply for shifts</p>
                    </div>
                  </div>
                  <span className="text-slate-400 hidden sm:inline">→</span>
                  <div className="flex items-center gap-2.5">
                    <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-emerald-100">
                      <ShieldCheck className="h-4.5 w-4.5 text-emerald-700" />
                    </span>
                    <div className="leading-tight">
                      <p className="text-[12.5px] font-bold text-emerald-800">Verified</p>
                      <p className="text-[11px] text-slate-500">Manager-confirmed</p>
                    </div>
                  </div>
                  <p className="sm:ml-auto text-[12px] text-slate-600 max-w-xs">
                    You see both statuses on every applicant card. You decide who to assign — we just make the truth visible.
                  </p>
                </div>
              </article>

              <hr className="border-slate-200" />

              {/* Pricing & Free Trial */}
              <article
                className="rounded-2xl bg-gradient-to-br from-[#F0FDF4] to-[#DCFCE7] border border-emerald-200 p-6 sm:p-7"
                data-testid="learn-more-pricing"
              >
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B132B] mb-3">Simple, honest pricing</h2>
                <p className="text-[15px] text-slate-700 leading-relaxed mb-2">
                  <strong>14-day free trial for every new manager — no card required.</strong>
                </p>
                <p className="text-[14.5px] text-slate-700 leading-relaxed mb-4">
                  Add a payment method any time before day 14 to keep using a paid plan. Otherwise you&apos;re automatically moved to Pay-As-You-Go so you only pay when a shift is successfully covered.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-4 text-[12.5px]">
                  {[
                    ['Pay As You Go', '£3.99 per completed shift, billed monthly'],
                    ['Monthly', '£29.99 / location / month'],
                    ['Annual', '£290 / location / year'],
                    ['Multi-site', '£99 / month · up to 5 locations'],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-white/70 border border-emerald-200 p-3">
                      <p className="text-[10.5px] uppercase tracking-wider text-emerald-700 font-semibold">{k}</p>
                      <p className="text-[13px] font-bold text-[#0B132B]">{v}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[13.5px] font-semibold text-slate-900 mb-1.5">Founding Member offer</p>
                <p className="text-[13px] text-slate-700 leading-relaxed mb-3">
                  The first <strong>20 paying Monthly customers</strong> lock in <strong>£19.99/month</strong> for as long as they maintain a continuous subscription. Cancel any time.
                </p>
                <p className="text-[13px] text-slate-600 leading-relaxed">
                  All plans include: full manager dashboard, notifications, till audit trail, and priority support (Monthly / Annual / Multi-site).
                </p>
              </article>

              <hr className="border-slate-200" />

              {/* Company Information */}
              <article data-testid="learn-more-company">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B132B] mb-4">Company Information</h2>
                <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-[14.5px]">
                  {[
                    ['Registered name', 'Shiftcover365 Ltd'],
                    ['ICO Registration', 'ZC161220'],
                    ['Companies House Number', '17187298'],
                    ['Country', 'United Kingdom'],
                    ['Support', 'UK-Based'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-2">
                      <dt className="text-slate-500">{k}</dt>
                      <dd className="font-semibold text-slate-900 text-right tabular-nums">{v}</dd>
                    </div>
                  ))}
                </dl>
              </article>

              <hr className="border-slate-200" />

              {/* FAQs */}
              <article data-testid="learn-more-faqs">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B132B] mb-6">Frequently Asked Questions</h2>
                <div className="space-y-2">
                  {[
                    {
                      q: 'Is ShiftCover365 approved by Post Office Ltd?',
                      a: (
                        <>
                          <p>ShiftCover365 is an independent platform built for retailers and customer-facing businesses, including Post Office branches.</p>
                          <p className="mt-2">We are not currently affiliated with or endorsed by Post Office Ltd.</p>
                        </>
                      ),
                    },
                    {
                      q: 'What happens to my data?',
                      a: (
                        <>
                          <p>All data is processed in accordance with UK GDPR.</p>
                          <p className="mt-2">We are ICO registered (ZC161220), and users can request a full export or deletion of their data from account settings.</p>
                          <p className="mt-2 font-semibold text-slate-900">We never sell user data.</p>
                        </>
                      ),
                    },
                    {
                      q: 'What if a worker does not show up?',
                      a: (
                        <>
                          <p>No-shows affect a worker&apos;s reliability score, which managers can view before approving future applications.</p>
                          <p className="mt-2">We do not use financial penalties.</p>
                          <p className="mt-2">Accountability is built through transparency, reliability history and visible records.</p>
                        </>
                      ),
                    },
                    {
                      q: 'Do I need a contract?',
                      a: (
                        <>
                          <p><strong className="font-semibold text-slate-900">No.</strong></p>
                          <p className="mt-2">There is no long-term contract. Cancel any time from Manager &rarr; Billing.</p>
                        </>
                      ),
                    },
                    {
                      q: 'When is the first payment collected?',
                      a: (
                        <>
                          <p>Every new manager starts with a <strong className="font-semibold text-slate-900">14-day free trial</strong> — no card required.</p>
                          <p className="mt-2">Add a payment method any time before day 14 to pick a paid plan (Monthly, Annual or Multi-site) — your first payment is taken the day you subscribe.</p>
                          <p className="mt-2">If you don&apos;t add a card, we automatically move you to Pay-As-You-Go on day 15 — you&apos;re only charged £3.99 per successfully completed shift, billed monthly.</p>
                        </>
                      ),
                    },
                    {
                      q: 'What are the paid plans?',
                      a: (
                        <>
                          <p>Pay-As-You-Go at <strong>£3.99 per successfully completed shift, billed monthly</strong>, Monthly at <strong>£29.99/location</strong>, Annual at <strong>£290/location</strong> (~£24.17/month equivalent), or Multi-site at <strong>£99/month for up to 5 locations</strong>.</p>
                          <p className="mt-2">The first 20 Monthly customers lock in a <strong>Founding Member</strong> rate of £19.99/month for as long as their subscription stays active.</p>
                        </>
                      ),
                    },
                    {
                      q: 'What does Smart ID actually check?',
                      a: (
                        <>
                          <p>Workers first self-declare their Smart ID.</p>
                          <p className="mt-2">A manager or administrator then confirms this before the worker is marked as fully verified.</p>
                          <p className="mt-2">This allows managers to clearly distinguish between self-declared workers and manager-confirmed workers before approving cover.</p>
                        </>
                      ),
                    },
                    {
                      q: 'Can I use my own staff?',
                      a: (
                        <>
                          <p><strong className="font-semibold text-slate-900">Yes.</strong></p>
                          <p className="mt-2">ShiftCover365 is designed to work with workers already known to your business, as well as additional workers invited or approved by your organisation.</p>
                        </>
                      ),
                    },
                    {
                      q: 'What records are kept?',
                      a: (
                        <p>ShiftCover365 maintains digital records relating to shifts, approvals, accountability information and activity history to help provide transparency for both business owners and workers.</p>
                      ),
                    },
                  ].map((item, idx) => (
                    <details
                      key={item.q}
                      className="group rounded-xl border border-slate-200 open:bg-slate-50/80 open:border-slate-300 transition-colors"
                      data-testid={`learn-more-faq-${idx + 1}`}
                    >
                      <summary className="cursor-pointer list-none px-4 py-3.5 flex items-center justify-between gap-3 text-[14.5px] font-semibold text-slate-900 select-none">
                        <span>{item.q}</span>
                        <ChevronDown className="h-4 w-4 text-slate-500 transition-transform group-open:rotate-180 shrink-0" />
                      </summary>
                      <div className="px-4 pb-4 text-[14px] text-slate-700 leading-relaxed">{item.a}</div>
                    </details>
                  ))}
                </div>
              </article>

              <hr className="border-slate-200" />

              {/* Final CTA */}
              <div className="text-center pt-2">
                <Button
                  className="rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white px-9 h-14 text-base font-semibold shadow-lg shadow-red-200/50"
                  onClick={() => goToTrial('learn-more-page-cta')}
                  data-testid="learn-more-cta-btn"
                >
                  Start Free Trial <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
                <p className="mt-3 text-[12.5px] text-slate-500">No card required · Cancel anytime</p>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-600 hover:text-slate-900"
              data-testid="learn-more-bottom-back-home"
            >
              <ArrowLeft className="h-4 w-4" /> Back to home
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
